import React, { useEffect, useState } from 'react'
import Modal from '../Modal'
import LoadingSpinner from '../LoadingSpinner'
import { useServiceContext } from '../../root'
import { PdfViewer } from '../PdfViewer/PdfViewer'
import { File as FileConstants } from '../../constants/file'
import type { FileDetail } from '../../services/api/files'
import { validateFileFactory } from '~/services/factory/validateFileFactory'

export interface MultiFilePreviewModalProps {
  fileId: Array<{ fsiId: number; fileFullPath: string; level: string }>
  onClose?: () => void
  modalClasses?: { modalClass?: string; backDropClass?: string }
}

const MultiFilePreviewModal: React.FC<MultiFilePreviewModalProps> = ({ fileId, onClose, modalClasses }) => {
  const context = useServiceContext()
  const { api, service } = context
  const { translate } = service.filter
  const [isOpen, setIsOpen] = useState(false)
  const fileArr = fileId.length ? fileId : []
  const [fsiId, setFsiId] = useState(fileArr[0].fsiId)
  const [fileFullPath, setFileFullPath] = useState(fileArr[0].fileFullPath)
  const [pdfLocation, setPdfLocation] = useState<string>('')
  const [file, setFile] = useState({} as FileDetail)
  const validateFile = validateFileFactory()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setIsOpen(true)
    loadData()
  }, [])

  function getPdfLocation(id: any) {
    const getService = function () {
      return api.files.getPdfUrlInShowFile(id)
    }
    return {
      service: getService,
      request: getService()
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const loadData = () => {
    setLoading(true)
    changePath(fsiId, fileFullPath)
    setLoading(false)
  }

  const changePath = (fsiId: number, path: string) => {
    setFsiId(fsiId)
    setFileFullPath(path)

    api.files.getDetails(fsiId).then(function (data) {
      setFile(data)
      setDownloadSourceDocFn()
    })

    // Use the correct API to get the PDF URL
    context.api.files
      .getPdfUrlInShowFile(fsiId)
      .then((result: any) => {
        if (result && typeof result.url === 'string') {
          setPdfLocation(result.url)
        } else {
          setPdfLocation('')
        }
      })
      .catch(() => {
        setPdfLocation('')
      })
  }

  function setDownloadSourceDocFn() {
    if (file.actionFlags && file.actionFlags.canDownloadSource) {
      return () => {
        validateFile.downloadOriginal(fsiId)
      }
    } else {
      return null
    }
  }

  return (
    <Modal
      show={true}
      handleClose={doClose}
      title={translate('FILE_DOCUMENT_PREVIEW')}
      className={`theme-sandman ${modalClasses?.modalClass || ''}`}
      dataTestId="multi-file-preview-modal"
      size="md"
    >
      <Modal.Body>
        <div>
          <div className="row">
            <div className="breadcrumbs-path">
              <table className="fs12 mb0 ml12 mr12 border-none">
                <tbody>
                  {fileArr.map((path, index) => (
                    <tr className="word-break" key={index}>
                      {path.level !== FileConstants.acl.BROWSE && path.level !== FileConstants.acl.NONE && (
                        <td className={`pt1-b0 ${path.fileFullPath === fileFullPath ? 'bold' : ''}`}>
                          <a className="cursor-pointer" onClick={() => changePath(path.fsiId, path.fileFullPath)}>
                            {path.fileFullPath}
                          </a>
                        </td>
                      )}
                      {path.level === FileConstants.acl.BROWSE && <td className="pt1-b0">{path.fileFullPath}</td>}
                      {path.level === FileConstants.acl.NONE && (
                        <td className="pt1-b0">{translate('PREVIEW_INSUFFICIENT_ACCESS_RIGHTS')}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ border: '1px solid lightgrey', minHeight: 800, margin: '16px 0' }}>
            {loading ? (
              <LoadingSpinner centered size={40} />
            ) : error ? (
              <div style={{ color: 'red', padding: 16 }}>{error}</div>
            ) : file ? (
              <PdfViewer
                src={pdfLocation}
                thumbnailId={file.thumbnailId}
                downloadFunction={setDownloadSourceDocFn}
                className="doc-pdf-container d-flex align-self-stretch flex-grow min-height"
              />
            ) : null}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          data-action={'ok'}
          className="btn btn-primary"
          id="0ca895eb-a127-45eb-b51d-24af3312700b"
          onClick={doClose}
        >
          {translate('OK')}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default MultiFilePreviewModal
